import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Input,
  Link,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { AiOutlineCheckCircle, AiOutlineWarning } from "react-icons/ai";
import { FaFacebookF, FaGoogle } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as Arrow } from "../arrow-trend-down-svgrepo-com (5).svg";
import { AuthContext } from "../context/AuthContext";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";

export default function Auth() {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";

  const [isLogin, setIsLogin] = useState(isLoginPage);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const { login, register, resetSuccessfulCreation } = useContext(AuthContext);
  const navigate = useNavigate();
  const { mode } = useColorScheme();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  const wasJustCreated = location.state?.successfulCreation;
  const [showSuccess, setShowSuccess] = useState(false);

  const passwordRef = useRef(null);
  const [showPasswords, setShowPasswords] = useState(false);

  const [warning, setWarning] = useState(""); // 🔴 ADDED
  const [emailWarning, setEmailWarning] = useState(""); // 🔴 ADDED

  // Sync state with URL
  useEffect(() => {
    setIsLogin(location.pathname === "/login");
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.successfulCreation) {
      setShowSuccess(true);
      setIsLogin(true);

      if (location.state.email) {
        setEmail(location.state.email);
        setPassword("");
      }

      // focus password AFTER render
      setTimeout(() => {
        passwordRef.current?.focus();
      }, 0);

      // clear router state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (location.state?.successfulCreation) {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  useEffect(() => {
    resetSuccessfulCreation?.();
  }, [resetSuccessfulCreation]);

  // Clear form and error when switching modes (no transition)
  const toggleMode = () => {
    setError("");
    setWarning("");

    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowSuccess(false);

    if (isLogin) {
      navigate("/register", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setWarning(""); // 🔴 ADDED
setEmailWarning("")
    // 🔹 Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email !== "" && !emailRegex.test(email)) {
setEmailWarning("Please enter a valid email address (e.g., example@domain.com).");
      return; // stop submission
    }

    if (isLogin) {
      try {
        await login(email, password);
        navigate("/dashboard");
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setError("Invalid email or password.");
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
      }
    } else {
      if (!name || !email || !password || !confirmPassword) {
        setWarning("Please fill all the fields.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }

      const ok = await register(name, email, password);
      if (ok) {
        navigate("/login", {
          state: {
            successfulCreation: true,
            email,
          },
          replace: true,
        });
      } else {
        setError("Registration failed. Please try again.");
      }
    }
  };

  const arrowProps = {
    component: Arrow,
    inheritViewBox: true,
    sx: {
      color: mode === "light" ? "#333" : "#fff",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      fontSize: isSmall ? 50 : isMedium ? 50 : 80,
      transform: "rotate(105deg)",
      width: "75%",
      marginBottom: "-100%",
    },
  };

  return (
    <Grid
      sx={{
        display: "flex",
        justifySelf: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        bgcolor: mode === "light" ? "#ebebeb" : "hsla(240, 7.70%, 2.50%, 0.15)",
        border:
          mode === "light"
            ? "0.5px solid hsla(240, 7.70%, 2.50%, 0.15)"
            : "0.5px solid rgba(229, 231, 235, 0.14)",
        flexDirection: "column",
        gap: { xs: 3, sm: 5 },
        width: "100%",
        maxWidth: isSmall ? "90%" : isMedium ? "90%" : "50%",
        marginTop: isSmall ? "5%" : isMedium ? "3.5%" : "2%",
        borderRadius: "8px",
        height: isLogin ? "70%" : "85%",
        transition: "all 0.3s ease",
        // removed built-in opacity/transform transition controlled by isTransitioning
      }}
    >
      {/* Top Arrow */}
      <Box
        sx={{
          position: "relative",
          marginLeft: "auto",
          alignSelf: "center",
          transform: {
            xs: "translateX(0%) translateY(-65%) rotate(-90deg) scaleX(-1)",
            sm: "translateX(25%) translateY(-120%) rotate(-90deg) scaleX(-1)",
            md: "translateX(0%) translateY(-65%) rotate(-90deg) scaleX(-1)",
          },
        }}
      >
        <SvgIcon {...arrowProps} />
      </Box>

      {/* Form */}
      <FormControl
        component="form"
        autoComplete="off"
        onSubmit={handleSubmit}
        sx={{
          gap: { xs: 2, sm: 2.5 },
          display: "flex",
          justifySelf: "center",
          height: "100%",
        }}
      >
        {/* Title */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography
            level={isSmall ? "h3" : "h2"}
            sx={{
              fontWeight: 500,
              fontFamily: "monospace",
              transition: "all 0.3s ease",
            }}
          >
            {isLogin ? "Login" : "Sign Up"}
          </Typography>
          {warning && (
            <Alert
              color="warning"
              variant="plain"
              sx={{ animation: "fadeIn 0.3s ease", background: "inherit" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AiOutlineWarning size={20} />
                <Typography
                  level="body-sm"
                  fontFamily="monospace"
                  color="warning"
                >
                  {warning}
                </Typography>
              </Stack>
            </Alert>
          )}
          {error && (
            <Alert
              color="danger"
              variant="plain"
              sx={{ animation: "fadeIn 0.3s ease", background: "inherit" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AiOutlineWarning size={20} />
                <Typography
                  level="body-sm"
                  fontFamily="monospace"
                  color="danger"
                >
                  {error}
                </Typography>
              </Stack>
            </Alert>
          )}
          {/* Success Alert */}
          {showSuccess && isLogin && (
            <Alert
              color="success"
              variant="plain"
              sx={{
                animation: "fadeIn 0.3s ease",
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "translateY(-10px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AiOutlineCheckCircle size={20} />
                <Typography level="body-sm" fontFamily="monospace">
                  Account has been successfully created.
                </Typography>
              </Stack>
            </Alert>
          )}
        </Stack>

        {/* Form Fields */}
        <Stack spacing={2}>
          {/* Name field (Register only) */}
          {!isLogin && (
            <Box
              sx={{
                animation: "slideIn 0.3s ease",
                "@keyframes slideIn": {
                  from: { opacity: 0, transform: "translateX(-20px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              <Typography
                level="body-sm"
                sx={{ fontFamily: "monospace", mb: 0.5 }}
              >
                Username
              </Typography>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={!isLogin}
                size={isSmall ? "sm" : "md"}
                color={
                  warning && !name ? "warning" : error ? "danger" : "neutral"
                }
              />
            </Box>
          )}

          {/* Email */}
          <Box>
            <Stack display={"flex"} direction={"row"} alignItems={"center"}>
              <Typography
                level="body-sm"
                sx={{ fontFamily: "monospace", mb: 0.5 }}
              >
                Email
              </Typography>
              <Alert
                color="warning"
                variant="plain"
                sx={{ animation: "fadeIn 0.3s ease", background: "inherit" }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    level="body-xs"
                    fontFamily="monospace"
                    color="warning"
                  >
                    {emailWarning}
                  </Typography>
                </Stack>
              </Alert>
            </Stack>

            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              size={isSmall ? "sm" : "md"}
              autoComplete="email"
              color={
                error ? "danger" : (warning && !email) ||emailWarning ? "warning" : "neutral"
              }
            />
          </Box>

          {/* Password */}
          <Box>
            <Typography
              level="body-sm"
              sx={{ fontFamily: "monospace", mb: 0.5 }}
            >
              Password
            </Typography>
            <Input
              type={showPasswords ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              ref={passwordRef}
              size={isSmall ? "sm" : "md"}
              autoComplete="new-password"
              endDecorator={
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => setShowPasswords((v) => !v)}
                >
                  {showPasswords ? <MdVisibilityOff /> : <MdVisibility />}
                </IconButton>
              }
              color={
                error ? "danger" : warning && !password ? "warning" : "neutral"
              }
            />
          </Box>

          {/* Confirm Password (Register only) */}
          {!isLogin && (
            <Box
              sx={{
                animation: "slideIn 0.3s ease",
              }}
            >
              <Typography
                level="body-sm"
                sx={{ fontFamily: "monospace", mb: 0.5 }}
              >
                Confirm Password
              </Typography>
              <Input
                type={showPasswords ? "text" : "password"} // <-- same state
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required={!isLogin}
                size={isSmall ? "sm" : "md"}
                autoComplete="chrome-off"
                name="confirm-password-field"
                readOnly
                onFocus={(e) => e.target.removeAttribute("readonly")}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}
                endDecorator={
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => setShowPasswords((v) => !v)} // <-- same toggle
                  >
                    {showPasswords ? <MdVisibilityOff /> : <MdVisibility />}
                  </IconButton>
                }
                color={
                  warning && !confirmPassword
                    ? "warning"
                    : error
                      ? "danger"
                      : "neutral"
                } // 🔴 ADDED
              />
            </Box>
          )}
        </Stack>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          type="submit"
          variant="outlined"
          fullWidth={isSmall}
          size={isSmall ? "md" : "lg"}
          sx={{
            color: mode === "light" ? "black" : "inherit",
            border:
              mode === "light"
                ? "0.5px solid hsla(240, 7.70%, 2.50%, 0.15)"
                : "0.5px solid rgba(229, 231, 235, 0.14)",
            width: { xs: "100%", sm: "50%", md: "40%" },
            boxShadow: "3.1px 6.2px 6.2px hsl(0deg 0% 0% / 0.40)",
            "&:hover": {
              transform: "translateY(-2px)",
            },
            alignSelf: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            mt: { xs: 2, sm: 3 },
          }}
        >
          <Typography fontFamily="monospace">
            {isLogin ? "Login" : "Register"}
          </Typography>
        </Button>
        {/* Social Login Buttons */}
        <Stack
          direction="row"
          justifyContent="center"
          spacing={2}
          alignItems="center"
        >
          <IconButton
            variant="outlined"
            size={isSmall ? "sm" : "md"}
            sx={{
              color: "inherit",
              border: "0px solid rgba(229, 231, 235, 0.14)",
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <Tooltip
              title={isLogin ? "Login with Google" : "Sign Up with Google"}
              arrow
            >
              <Typography>
                <FaGoogle size={isSmall ? 20 : 25} />
              </Typography>
            </Tooltip>
          </IconButton>

          <IconButton
            variant="outlined"
            size={isSmall ? "sm" : "md"}
            sx={{
              color: "inherit",
              border: "0px solid rgba(229, 231, 235, 0.14)",
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <Tooltip
              title={isLogin ? "Login with Facebook" : "Sign Up with Facebook"}
              arrow
            >
              <Typography>
                <FaFacebookF size={isSmall ? 20 : 25} />
              </Typography>
            </Tooltip>
          </IconButton>
        </Stack>

        <Divider>Or</Divider>

        {/* Toggle Link */}
        <Link
          component="button"
          underline="none"
          onClick={toggleMode}
          sx={{
            alignSelf: "center",
            fontFamily: "monospace",
            fontSize: { xs: "0.875rem", sm: "1rem" },
            textAlign: "center",
            cursor: "pointer",
            position: "relative",
            color: mode === "light" ? "#555E68" : "inherit",
            transition: "all 0.3s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-2px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              height: "0.2px",
              backgroundColor: "currentColor",
              transition: "width 0.3s ease",
            },
            "&:hover::after": {
              width: "0%",
            },
          }}
        >
          {isLogin
            ? "Don't have an account? Create one"
            : "Already have an account? Sign in"}
        </Link>
      </FormControl>

      {/* Bottom Arrow */}
      <Box
        sx={{
          position: "relative",
          alignSelf: "start",
          transform: {
            xs: "translateX(-65%) translateY(-40%) rotate(0)",
            sm: isLogin
              ? "translateX(-80%) translateY(-40%) rotate(0)"
              : "translateX(-80%) translateY(-150%) rotate(0)",
            md: isLogin
              ? "translateX(-65%) translateY(-60%) rotate(0)"
              : "translateX(-65%) translateY(-150%) rotate(0)",
          },
        }}
      >
        <SvgIcon {...arrowProps} />
      </Box>
    </Grid>
  );
}
